import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export interface SummaryItem {
  firstName: string;
  lastName: string;
  secondary?: string;
  urgency?: "expired" | "urgent" | "normal";
  onClick?: () => void;
}

interface SummaryCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  count: number;
  accentColor: string;
  accentBg: string;
  avatarColor: string;
  avatarBg: string;
  items: SummaryItem[];
  loading?: boolean;
  emptyText?: string;
}

const PREVIEW_LIMIT = 3;

const getInitials = (firstName: string, lastName: string): string =>
  `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();

const SummaryCardSkeleton: React.FC = () => (
  <div className="sc-card">
    <div className="sc-header">
      <div className="sc-skeleton sc-skeleton--icon" />
      <div className="sc-skeleton-meta">
        <div className="sc-skeleton sc-skeleton--title" />
        <div className="sc-skeleton sc-skeleton--subtitle" />
      </div>
      <div className="sc-skeleton sc-skeleton--count" />
    </div>
    <div className="sc-divider" />
    <div className="sc-list">
      {[1, 2, 3].map((i) => (
        <div className="sc-item" key={i}>
          <div className="sc-skeleton sc-skeleton--avatar" />
          <div className="sc-skeleton sc-skeleton--name" />
          <div className="sc-skeleton sc-skeleton--secondary" />
        </div>
      ))}
    </div>
  </div>
);

const SummaryCard: React.FC<SummaryCardProps> = ({
  icon,
  title,
  subtitle,
  count,
  accentColor,
  accentBg,
  avatarColor,
  avatarBg,
  items,
  loading = false,
  emptyText = "No items",
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (loading) return <SummaryCardSkeleton />;

  const visible = expanded ? items : items.slice(0, PREVIEW_LIMIT);
  const overflow = items.length - PREVIEW_LIMIT;

  return (
    <div className="sc-card">
      <div className="sc-header">
        <div className="sc-icon-wrap" style={{ background: accentBg }}>
          <span className="sc-icon" style={{ color: accentColor }}>
            {icon}
          </span>
        </div>
        <div className="sc-meta">
          <span className="sc-title">{title}</span>
          <span className="sc-subtitle">{subtitle}</span>
        </div>
        <span className="sc-count" style={{ color: accentColor }}>
          {count}
        </span>
      </div>

      <div className="sc-divider" />

      <div className="sc-list">
        {visible.length > 0 ? (
          <>
            {visible.map((item, i) => {
              const avatarStyle =
                item.urgency === "expired"
                  ? { background: "#fecaca", color: "#b91c1c" }
                  : item.urgency === "urgent"
                    ? { background: "#fde68a", color: "#92400e" }
                    : { background: avatarBg, color: avatarColor };
              const secondaryClass =
                item.urgency === "expired"
                  ? "sc-secondary sc-secondary--expired"
                  : item.urgency === "urgent"
                    ? "sc-secondary sc-secondary--urgent"
                    : "sc-secondary";
              return (
                <div
                  className={`sc-item${item.onClick ? " sc-item--clickable" : ""}`}
                  key={i}
                  onClick={item.onClick}
                >
                  <div className="sc-avatar" style={avatarStyle}>
                    {getInitials(item.firstName, item.lastName)}
                  </div>
                  <span className="sc-name">
                    {item.firstName} {item.lastName}
                  </span>
                  {item.secondary && (
                    <span className={secondaryClass}>{item.secondary}</span>
                  )}
                </div>
              );
            })}
            {!expanded && overflow > 0 && (
              <button
                className="sc-more sc-more--btn"
                onClick={() => setExpanded(true)}
              >
                +{overflow} more
              </button>
            )}
            {expanded && items.length > PREVIEW_LIMIT && (
              <button
                className="sc-more sc-more--btn"
                onClick={() => setExpanded(false)}
              >
                {t("Show less")}
              </button>
            )}
          </>
        ) : (
          <div className="sc-empty">{emptyText}</div>
        )}
      </div>
    </div>
  );
};

export default SummaryCard;
